import type { ReactNode } from 'react';

interface CardCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  icon?: ReactNode;
  children?: ReactNode;
  colorTheme?: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'cyan';
  className?: string;
  padding?: 'sm' | 'md';
}

const colorClasses: Record<string, string> = {
  blue:   'text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-600',
  purple: 'text-purple-600 focus:ring-purple-500 dark:focus:ring-purple-600',
  green:  'text-green-600 focus:ring-green-500 dark:focus:ring-green-600',
  orange: 'text-orange-600 focus:ring-orange-500 dark:focus:ring-orange-600',
  red:    'text-red-600 focus:ring-red-500 dark:focus:ring-red-600',
  cyan:   'text-cyan-600 focus:ring-cyan-500 dark:focus:ring-cyan-600',
};

export default function CardCheckbox({
  checked,
  onChange,
  disabled = false,
  label,
  description,
  icon,
  children,
  colorTheme = 'blue',
  className,
  padding = 'md'
}: CardCheckboxProps) {
  const isSmall = padding === 'sm';

  const labelClasses = [
    'flex items-start space-x-3',
    isSmall ? 'p-2' : 'p-3',
    'rounded-lg border border-gray-200 dark:border-gray-600',
    disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer',
    'transition-colors',
    className ?? ''
  ].join(' ');

  const inputClasses = [
    isSmall ? 'mt-0.5' : 'mt-1',
    'w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 cursor-pointer disabled:cursor-not-allowed',
    colorClasses[colorTheme]
  ].join(' ');

  return (
    <label className={labelClasses}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className={inputClasses}
      />
      <div className="flex-1">
        {children ?? (
          <>
            {(icon !== undefined || label !== undefined) && (
              <div className="flex items-center">
                {icon}
                {label !== undefined && (
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                )}
              </div>
            )}
            {description !== undefined && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {description}
              </p>
            )}
          </>
        )}
      </div>
    </label>
  );
}
